import axios from 'axios';
import { getListId } from '../utils/listIdConfig';
import { apiPath, getApiBaseUrl } from '../config/apiConfig';

const UPSTREAM_API = 'https://api.bonitas.co.za';

function getApiBase() {
  return getApiBaseUrl();
}

function apiUrl(path) {
  return apiPath(path);
}

// API keys only when calling Bonitas upstream directly from the browser (legacy).
// When REACT_APP_API_BASE_URL points at your API server, that server adds upstream keys.
const API_KEYS = {
  AGILE: process.env.REACT_APP_AGILE_API_KEY || '',
  BONITAS: process.env.REACT_APP_BONITAS_API_KEY || '',
  SUBMISSIONS: process.env.REACT_APP_SUBMISSIONS_API_KEY || '',
};

function shouldSendClientApiKeys() {
  return getApiBase() === UPSTREAM_API && !process.env.REACT_APP_API_BASE_URL;
}

/**
 * Get the appropriate Bonitas endpoint based on plan type and environment
 * @param {string} planType - The selected plan (e.g., 'PL470' for BonCap)
 * @param {string} environment - The environment (development, production, etc.)
 * @returns {string} The appropriate API endpoint URL
 */
export function getFullFormEndpoint(planType, environment) {
  const isBonCap = planType === 'PL470';

  if (isBonCap) {
    return apiUrl('/submit-hubv2-application');
  }

  return apiUrl('/submit-application');
}

/**
 * Submit a quote from the Landing Page to HubV2 endpoint
 * @param {object} formData - The form data from Landing Page
 * @returns {Promise} Axios response
 */
export async function submitLandingPageQuote(formData) {
  const payload = {
    firstName: formData.firstName || '',
    lastName: formData.lastName || '',
    age: formData.age || '',
    cellNumber: formData.cellNumber || '',
    email: formData.email || '',
    consent: formData.consent || false
  };

  const endpoint = apiUrl('/submit-hubv2-application');
  console.log('[Landing Page] Submitting to HubV2:', endpoint, payload);

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (shouldSendClientApiKeys()) {
      headers['x-api-key'] = API_KEYS.BONITAS;
    }

    const response = await axios.post(endpoint, payload, { headers });
    
    console.log('[Landing Page] HubV2 Response:', response.data);
    return response;
  } catch (error) {
    console.error('[Landing Page] HubV2 Error:', error);
    throw error;
  }
}

/**
 * Submit a lead to Agile Infinity
 * @param {object} leadData - The lead data
 * @returns {Promise} Axios response with uuid
 */
export async function submitAgileLead(leadData) {
  const payload = {
    FirstName: leadData.FirstName || '',
    LastName: leadData.LastName || '',
    MobilePhone: leadData.MobilePhone || '',
    Email: leadData.Email || '',
    LeadChannel: leadData.LeadChannel || 'Website',
    LeadProvider: leadData.LeadProvider || 'Bonitas Website',
    SubmitProduct: leadData.SubmitProduct || 'Medical Aid',
    RSAId: leadData.RSAId || '',
    ListID: leadData.ListID ?? getListId(),
    SourceLeadID: leadData.SourceLeadID || 'Bonitas website',
    Age: leadData.Age || null
  };

  const endpoint = apiUrl('/submit-agile-application');
  console.log('[Agile] Submitting lead:', endpoint, payload);

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (shouldSendClientApiKeys()) {
      headers['x-api-key'] = API_KEYS.AGILE;
    }

    const response = await axios.post(endpoint, payload, { headers });

    console.log('[Agile] Response:', response.data);
    
    if (response.data && response.data.status === 'success' && response.data.uuid) {
      return response.data.uuid;
    } else {
      throw new Error('Failed to get UUID from Agile: ' + (response.data?.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('[Agile] Error:', error);
    throw error;
  }
}

/**
 * Submit full application to Bonitas
 * @param {object} applicationData - The complete application payload
 * @param {string} planType - The selected plan type
 * @param {string} environment - The environment
 * @returns {Promise} Axios response
 */
export async function submitBonitasApplication(applicationData, planType, environment) {
  const endpoint = getFullFormEndpoint(planType, environment);
  
  console.log('[Bonitas] Submitting to:', endpoint);
  console.log('[Bonitas] Payload:', applicationData);

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (shouldSendClientApiKeys()) {
      headers['x-api-key'] = API_KEYS.BONITAS;
    }

    const response = await axios.post(endpoint, applicationData, { headers });

    console.log('[Bonitas] Response:', response.data);
    return response;
  } catch (error) {
    console.error('[Bonitas] Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      payload: JSON.stringify(applicationData, null, 2)
    });
    throw error;
  }
}

/**
 * Persist form submission to MySQL (audit trail; non-blocking for applicant UX).
 * @param {object} payload - { status, planCode, agileLeadId, formData, apiPayload, bonitasError }
 * @returns {Promise} Axios response
 */
export async function saveFormSubmission(payload) {
  if (!API_KEYS.SUBMISSIONS) {
    console.warn('[Submissions] REACT_APP_SUBMISSIONS_API_KEY not set; skipping save');
    return null;
  }

  try {
    const response = await axios.post(
      apiUrl('/save-form-submission'),
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEYS.SUBMISSIONS,
        },
      }
    );
    console.log('[Submissions] Saved:', response.data);
    return response;
  } catch (error) {
    console.error('[Submissions] Save error:', error.message);
    throw error;
  }
}

export default {
  submitLandingPageQuote,
  submitAgileLead,
  submitBonitasApplication,
  saveFormSubmission,
  getFullFormEndpoint,
};
